/**
 * Hook to manage an auto-rotating session API key for the conversion UI.
 *
 * - On mount, checks sessionStorage for an existing key
 * - Validates a stored key against the API before trusting it (a key may be
 *   expired client-side OR rejected server-side — e.g. revoked by the
 *   single-session policy, or left over from a different database after a
 *   prod-copy swap). A stored key that the server rejects with 401 is
 *   discarded and a fresh one is minted.
 * - If no key / expired / rejected, creates a new one via tRPC
 * - Returns the key, a masked version for display, loading state, errors, and
 *   `invalidateKey()` to self-heal when a downstream call sees a 401.
 */

import type { SitzungsschluesselZustand } from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { trpc } from '@/trpc'
import { saasConfig } from '../saas.config'

// Storage key v2 — the v1 name ("temp-api-key") may still hold a stale
// (revoked) key from the previous createTemporary implementation; using a new
// name guarantees we start clean.
const SESSION_STORAGE_KEY = 'session-api-key-v2'
const LEGACY_STORAGE_KEY = 'temp-api-key'
const EXPIRY_BUFFER_MS = 5 * 60 * 1000 // 5 minutes
const VALIDATE_TIMEOUT_MS = 5000

interface StoredKey {
  key: string
  expiresAt: string
}

/**
 * Stand des Sitzungsschlüssels für die Konvertieren-Karte (KW-003, eine Tür).
 *
 * Ein vorhandener Schlüssel konvertiert, auch während er nachgeprüft wird. Ohne Schlüssel heißt
 * „lädt“ noch warten, sonst fehlt er (Fehler, Stundenlimit 20 je Nutzer) — die Karte sperrt dann
 * den Knopf und nennt den Grund.
 */
export function sitzungsschluesselZustand({
  apiKey,
  isLoading,
}: {
  apiKey: string | null
  isLoading: boolean
}): SitzungsschluesselZustand {
  if (apiKey) return 'da'
  return isLoading ? 'laedt' : 'fehlt'
}

interface UseTempApiKeyResult {
  apiKey: string | null
  maskedApiKey: string | null
  isLoading: boolean
  error: string | null
  /** Discard the current key and mint a fresh one (call this on a 401). */
  invalidateKey: () => void
  /**
   * Discard the current key, mint a fresh one and hand it back — for a caller that wants to repeat the rejected
   * request with the new key (403 after an upgrade: the session key lives 1 h and carries the entitlements of the
   * moment it was minted, plan API 2026-11-01/01 E10). `null` when no key could be minted.
   */
  refreshKey: () => Promise<string | null>
}

function maskKey(key: string): string {
  if (key.length <= 24) return key
  return `${key.slice(0, 20)}****${key.slice(-4)}`
}

function getStoredKey(): StoredKey | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed: StoredKey = JSON.parse(raw)
    if (!parsed.key || !parsed.expiresAt) return null
    return parsed
  } catch {
    return null
  }
}

function isNotExpired(stored: StoredKey): boolean {
  const expiresAt = new Date(stored.expiresAt).getTime()
  const now = Date.now()
  return expiresAt - now > EXPIRY_BUFFER_MS
}

function storeKey(key: string, expiresAt: string): void {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ key, expiresAt }))
}

function clearStoredKey(): void {
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}

function getApiBaseUrl(): string {
  return saasConfig.api.baseUrl || window.location.origin
}

/**
 * Probe whether a stored key is still accepted by the server. Hits the cheap,
 * auth-only `GET /api/v1/invoice/formats` endpoint. A 401 means the key was
 * revoked / expired server-side / belongs to a different database — it must be
 * discarded. Any other outcome (2xx, 403, 429, network error) is treated as
 * "key still authenticates", so a transient failure never nukes a good key.
 */
export async function isKeyAcceptedByServer(key: string): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), VALIDATE_TIMEOUT_MS)
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/v1/invoice/formats`, {
      headers: { Authorization: `Bearer ${key}` },
      signal: controller.signal,
    })
    return res.status !== 401
  } catch {
    // Network error / abort — don't discard the key on a transient failure.
    return true
  } finally {
    clearTimeout(timer)
  }
}

export function useTempApiKey(): UseTempApiKeyResult {
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [maskedApiKey, setMaskedApiKey] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Errors are handled locally (inline degradation note on the conversion
  // page) — the global raw-message toast would double-report them.
  const createMutation = trpc.apiKey.createSessionKey.useMutation({
    meta: { suppressGlobalErrorToast: true },
  })
  const hasInitialized = useRef(false)

  const fetchKey = useCallback(async (): Promise<string | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await createMutation.mutateAsync()
      // tRPC may serialize Date as string
      const expiresAt =
        typeof result.expiresAt === 'string'
          ? result.expiresAt
          : (result.expiresAt as Date).toISOString()

      storeKey(result.key, expiresAt)
      setApiKey(result.key)
      setMaskedApiKey(maskKey(result.key))
      return result.key
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create session API key'
      setError(message)
      clearStoredKey()
      setApiKey(null)
      setMaskedApiKey(null)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [createMutation])

  // Self-heal: drop the stored key and mint a fresh one. Wire this to a 401
  // from any downstream REST call (e.g. a key revoked mid-session).
  const invalidateKey = useCallback(() => {
    clearStoredKey()
    setApiKey(null)
    setMaskedApiKey(null)
    void fetchKey()
  }, [fetchKey])

  const refreshKey = useCallback(() => {
    clearStoredKey()
    setApiKey(null)
    setMaskedApiKey(null)
    return fetchKey()
  }, [fetchKey])

  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    // Drop any v1 "temp-api-key" entry — those came from the legacy
    // createTemporary mutation and may now be revoked in the DB.
    sessionStorage.removeItem(LEGACY_STORAGE_KEY)

    void (async () => {
      const stored = getStoredKey()
      if (stored && isNotExpired(stored)) {
        // The key looks valid by its expiry, but it may have been revoked
        // server-side or carried over from a different database. Confirm the
        // server still accepts it before trusting it.
        if (await isKeyAcceptedByServer(stored.key)) {
          setApiKey(stored.key)
          setMaskedApiKey(maskKey(stored.key))
          setIsLoading(false)
          return
        }
        clearStoredKey()
      }

      // No key, expired, or rejected by the server — mint a fresh one.
      await fetchKey()
    })()
  }, [fetchKey])

  return { apiKey, maskedApiKey, isLoading, error, invalidateKey, refreshKey }
}
